import { useContext, useState } from "react";
import { Controller, useFieldArray } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  ButtonContainer,
  DeleteButton,
  PrimaryButtonLargeV2,
  SecondaryButtonWithPlusIcon,
  TertiaryButtonLargeV2,
} from "../../../../../components/Buttons/Buttons";
import { Form } from "../../../../../layout/FormLayout";
import type {
  AttributeGroupSummary,
  TemplateType,
} from "../../../../../types/types.PIM";
import { useFormWrapper, useStoreState } from "../../../../../util/util";
import type { AxiosError } from "axios";
import Axios from "axios";
import { Notifications } from "../../../../../components/Notifications/NotificationsContext";
import { SelectWithDeleteButton } from "../../../../../layout/shared";
import * as z from "zod";
import {
  zodSelectBoxDefault,
  zodSelectBoxType,
} from "../../../../../util/zod.util";
import { zodResolver } from "@hookform/resolvers/zod";
import { MarginBottomH6 } from "../SellerAdminPIMAttributes/CreateAttribute";
import { findNonEmptyDuplicateIndexes } from "../../../../../util/form.utils";
import { ConfirmDialog } from "../../../../../components/ConfirmDialog/ConfirmDialog";
import { SearchSelectInfiniteScroll } from "../../../../../components/SearchSelectInfiniteScroll/SearchSelectInfiniteScroll";
import { attributeSearchOptions } from "../SellerAdminPIMAttributesCollections/util";
import { GroupAttributeListGridViewModeRadios } from "../../../../../components/ObjectReference/GroupAttributeListGridViewModeRadios";
import type {
  AttributeOption,
  DisplayConfigurationViewMode,
} from "../../../../../components/ObjectReference/objectReference.types";
import {
  isProductTargetObjectReference,
  shouldIncludeObjectReferenceByTemplateEntity,
} from "../../../../../components/ObjectReference/utils";
import { spacingTokens } from "../../../../../designTokens";

/**
 * Extracts name from label.
 * @param label is structured this way: `{{name}} ({{input_type}})`
 * @returns string which is the {{name}}
 */
export const getNameFromLabel = (label: string): string => {
  const stringArr = label.split("(");
  stringArr.pop();
  return stringArr.join("(").trim();
};

const AddAttributeToGroupSchema = z.object({
  first_attribute: zodSelectBoxType,
  group: z
    .object({
      attribute: zodSelectBoxType,
    })
    .array()
    .optional(),
});

const AddAttributeToGroupSchemaFn = (
  t: (s: string) => string,
  attributes: string[]
) =>
  z
    .object({
      first_attribute: zodSelectBoxDefault(t).refine(
        ({ label }) =>
          findNonEmptyDuplicateIndexes([getNameFromLabel(label), ...attributes])
            .length === 0,
        t("Please remove all duplicate attributes")
      ),
      group: z
        .object({
          attribute: zodSelectBoxDefault(t),
        })
        .array()
        .optional(),
    })
    .superRefine(({ first_attribute, group }, ctx) => {
      const duplicateIdxs = group
        ? findNonEmptyDuplicateIndexes([
            getNameFromLabel(first_attribute.label),
            ...attributes,
            ...group.map(({ attribute: { label } }) => getNameFromLabel(label)),
          ])
        : [];
      if (duplicateIdxs.length > 0) {
        duplicateIdxs.forEach((idx) => {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t("Duplicate attributes not allowed"),
            path: [`group[${idx - (attributes.length + 1)}].attribute`],
          });
        });
      }
    });

type formOutput = z.infer<typeof AddAttributeToGroupSchema>;

export function AddAttributeToExistingGroup({
  group: { id: group_id, attributes: existingAttributes },
  onSuccess,
  isTemplateInUse = false,
  templateEntityType,
  onCancel,
  templateType,
}: {
  group: AttributeGroupSummary;
  onSuccess: () => void;
  templateType: TemplateType | null;
  isTemplateInUse?: boolean;
  templateEntityType?: string;
  onCancel: () => void;
}) {
  const [showDialog, setShowDialog] = useState(false);
  const [formValue, setFormValue] = useState<formOutput>();
  const [viewModeByAttributeId, setViewModeByAttributeId] = useState<
    Record<string, DisplayConfigurationViewMode>
  >({});
  const { t } = useTranslation();
  const { tenant_id } = useStoreState();
  const entityName =
    templateType === "product" || templateType == null
      ? "products"
      : "profiles";
  const methodsOfUseForm = useFormWrapper({
    resolver: zodResolver(
      AddAttributeToGroupSchemaFn(
        t,
        existingAttributes.map(({ name }) => name)
      )
    ),
    defaultValues: {
      first_attribute: { label: undefined, value: undefined },
    },
  });
  const { handleSubmit, control, formState, errors, watch } = methodsOfUseForm;
  const { notifyError, notifySuccess } = useContext(Notifications);

  const { fields, remove, append } = useFieldArray({
    control,
    name: "group",
  });

  const getFilteredAttributeOptions = (
    attributes: AttributeGroupSummary["attributes"]
  ) =>
    attributeSearchOptions(
      attributes.filter((attribute) =>
        shouldIncludeObjectReferenceByTemplateEntity(
          attribute,
          templateEntityType
        )
      )
    );

  const selectedFirstAttribute = watch("first_attribute") as
    | AttributeOption
    | undefined;
  const selectedGroupAttributes =
    (watch("group") as { attribute?: AttributeOption }[] | undefined) ?? [];

  const shouldShowGroupAttributeViewMode = (attribute?: AttributeOption) =>
    Boolean(attribute?.value) && isProductTargetObjectReference(attribute);

  const onViewModeChange =
    (attributeId: string) => (e: React.FormEvent<HTMLSelectElement>) => {
      const viewMode = e.currentTarget.value as DisplayConfigurationViewMode;
      if (!viewMode) {
        return;
      }
      setViewModeByAttributeId((prev) => ({
        ...prev,
        [attributeId]: viewMode,
      }));
    };

  const createFields = (): JSX.Element[] => {
    return fields.map((field, index: number) => (
      <div key={field.id}>
        <SelectWithDeleteButton>
          <Controller
            key={field.id}
            as={SearchSelectInfiniteScroll}
            control={control}
            name={`group[${index}].attribute`}
            defaultValue={{ label: "", value: "" }}
            placeholder={t("Select Attribute")}
            baseUrl={`/v2/tenants/${tenant_id}/pim/attributes`}
            getOptions={getFilteredAttributeOptions}
            errors={{
              [`group[${index}].attribute`]:
                errors?.group?.[index]?.attribute?.value ??
                errors?.group?.[index]?.attribute ??
                undefined,
            }}
            formState={formState}
            defaultOptions
          />
          <DeleteButton
            testid={`delete-button-${index}`}
            onClick={() => remove(index)}
            type="button"
            height={20}
            width={20}
          />
        </SelectWithDeleteButton>
        {shouldShowGroupAttributeViewMode(
          selectedGroupAttributes[index]?.attribute
        ) && (
          <GroupAttributeListGridViewModeRadios
            namePrefix={`group_attribute_view_mode_${index}`}
            currentViewMode={
              viewModeByAttributeId[
                selectedGroupAttributes[index].attribute?.value ?? ""
              ] ?? "link"
            }
            onViewModeChange={onViewModeChange(
              selectedGroupAttributes[index].attribute?.value ?? ""
            )}
          />
        )}
      </div>
    ));
  };

  const processForm = async (values: formOutput) => {
    const attributeIds = [
      values.first_attribute.value,
      ...(values?.group?.map((attr) => attr?.attribute?.value) ?? []),
    ].filter((id): id is string => Boolean(id));

    const attributesToAdd = {
      attribute_ids: attributeIds,
      display_configuration_by_attribute_id: attributeIds.reduce<
        Record<string, DisplayConfigurationViewMode>
      >((acc, attributeId) => {
        const selectedMode = viewModeByAttributeId[attributeId];
        if (selectedMode) {
          acc[attributeId] = selectedMode;
        }
        return acc;
      }, {}),
    };

    try {
      await Axios.post(
        `/v2/tenants/${tenant_id}/pim/groups/${group_id}/attributes`,
        attributesToAdd
      );
      notifySuccess(t("Attribute(s) added successfully"));
      onSuccess();
    } catch (error) {
      const errorMessage = (error as AxiosError)?.response?.data?.message;
      notifyError(
        errorMessage
          ? errorMessage
          : t("There was an error modifying the group"),
        {
          error,
        }
      );
    }
  };

  const onSubmit = async (values: formOutput) => {
    if (isTemplateInUse) {
      setShowDialog(true);
      setFormValue(values);
    } else {
      await processForm(values);
    }
  };

  return (
    <>
      <Form noValidate onSubmit={handleSubmit(onSubmit)}>
        <MarginBottomH6>
          {t("Select from existing list of attributes")}
        </MarginBottomH6>
        <div style={{ width: "420px" }}>
          <Controller
            as={SearchSelectInfiniteScroll}
            control={control}
            name="first_attribute"
            baseUrl={`/v2/tenants/${tenant_id}/pim/attributes`}
            getOptions={getFilteredAttributeOptions}
            placeholder={t("Select Attribute")}
            errors={errors}
            formState={formState}
            defaultOptions
          />
        </div>
        {shouldShowGroupAttributeViewMode(selectedFirstAttribute) && (
          <GroupAttributeListGridViewModeRadios
            namePrefix="first_attribute_view_mode"
            currentViewMode={
              viewModeByAttributeId[selectedFirstAttribute?.value ?? ""] ??
              "link"
            }
            onViewModeChange={onViewModeChange(
              selectedFirstAttribute?.value ?? ""
            )}
          />
        )}
        {createFields()}
        <SecondaryButtonWithPlusIcon
          type="button"
          style={{ marginBottom: spacingTokens.spacing_5xl }}
          onClick={() => append({ name: "" })}
        >
          {t("Add attribute")}
        </SecondaryButtonWithPlusIcon>
        <ButtonContainer style={{ marginTop: "30px" }}>
          <TertiaryButtonLargeV2 fullWidth type="reset" onClick={onCancel}>
            {t("Cancel")}
          </TertiaryButtonLargeV2>
          <PrimaryButtonLargeV2 fullWidth type="submit">
            {t("Add")}
          </PrimaryButtonLargeV2>
        </ButtonContainer>
      </Form>
      <ConfirmDialog
        show={showDialog}
        closeDialog={() => {
          setFormValue(undefined);
          setShowDialog(false);
        }}
        confirmMessage={t(
          `Any changes will be pushed down to all ${entityName} using this template. Do you want to continue?`
        )}
        handleConfirm={async () => {
          await processForm(formValue!);
          setShowDialog(false);
        }}
        confirmBtnLabel={t("Save")}
        confirmBtnType="confirm"
        title={t("Save Changes")}
        useV2Buttons={true}
      />
    </>
  );
}
